import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import ContactForm from './components/ContactForm';
import AnalyticsTracker from './components/AnalyticsTracker';
import { Canvas, useFrame } from "@react-three/fiber";


import {
  MeshTransmissionMaterial,
  Float,
  TorusKnot,
  Environment,
  Lightformer,
  Sparkles,
  MeshDistortMaterial,
  Sphere
} from "@react-three/drei";
import Cursor from './components/Cursor';
import Aurora from './components/Aurora';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useInView,
  AnimatePresence,
} from "framer-motion";
import {
  Menu,
  X,
  Play,
  Maximize2,
  Shield,
  Smartphone,
  BarChart3,
  ArrowUpRight,
  Activity,
  Globe,
  Zap,
  Layers,
  Code2,
  Cpu as CpuIcon,
  ArrowRight,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
  Award,
  Cpu,
  Palette,
  Gamepad2,
  Monitor,
  MessageCircle,
  Home,
  Car,
  Glasses,
  Bot,
  ChevronDown,
  Box,
  CheckCircle2,
  PhoneCall,
  Building2,
  Rocket,
  Plus,
  Minus,
  Settings,
  MapPin,
  ChevronRight,
    Users
} from "lucide-react";

// --- LAZY-LOADED PAGES (each page becomes its own JS chunk) ---
const ServicesPage        = lazy(() => import('./pages/Services'));
const AboutUs             = lazy(() => import('./pages/AboutUs'));
const Portfolio           = lazy(() => import('./pages/Portfolio'));
const TermsOfService      = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy       = lazy(() => import('./pages/PrivacyPolicy'));
const RealEstate          = lazy(() => import('./pages/RealEstate'));
const ARVR                = lazy(() => import('./pages/ARVR'));
const GameDevelopment     = lazy(() => import('./pages/GameDevelopment'));
const AiPlatforms         = lazy(() => import('./pages/AiPlatforms'));
const EnterpriseInteractive = lazy(() => import('./pages/EnterpriseInteractive'));
const Automotive          = lazy(() => import('./pages/Automotive'));
const Blogs               = lazy(() => import('./pages/Blogs'));
const BlogDetail          = lazy(() => import('./pages/BlogDetail'));
const ProjectDetail       = lazy(() => import('./pages/ProjectDetail'));




// --- UTILS & GLOBAL STYLES ---
const MetallicText = ({ children, className = "" }) => (
  <span className={`bg-clip-text text-transparent bg-gradient-to-r from-black via-zinc-400 to-zinc-600 animate-gradient-x ${className}`}>
    {children}
  </span>
);



// --- 3D HERO COMPONENT ---
const GlassCrystal = () => {
  const mesh = useRef(null);

  useFrame((state) => {
    if (mesh.current) {
      mesh.current.rotation.x = state.clock.getElapsedTime() * 0.2;
      mesh.current.rotation.y = state.clock.getElapsedTime() * 0.2;
    }
  });

  return (
    <group>
      <Float speed={2} rotationIntensity={1.5} floatIntensity={2}>
        <TorusKnot args={[1, 0.3, 128, 32]} ref={mesh}>
          <MeshTransmissionMaterial 
             backside
             samples={16}
             resolution={1024}
             transmission={0.95}
             roughness={0.1}
             clearcoat={1}
             clearcoatRoughness={0.1}
             thickness={1}
             ior={1.5}
             chromaticAberration={0.06}
             anisotropy={0.3}
             distortion={0.4}
             distortionScale={0.4}
             temporalDistortion={0.2}
             color="#020617" 
             background="white"
          />
        </TorusKnot>
      </Float>
      <Environment resolution={512}>
          <group rotation={[-Math.PI / 3, 0, 1]}>
            <Lightformer form="circle" intensity={4} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={2} color="white"/>
            <Lightformer form="circle" intensity={2} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={2} color="#e4e4e7"/> 
            <Lightformer form="circle" intensity={2} rotation-y={Math.PI / 2} position={[-5, -1, -1]} scale={2} color="white"/>
            <Lightformer form="circle" intensity={2} rotation-y={-Math.PI / 2} position={[10, 1, 0]} scale={8} color="white"/>
          </group>
      </Environment>
    </group>
  );
};



const AmbientBackground = () => {
    return (
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden bg-[#fafafa]">
            {/* Subtle Noise Texture */}
            <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" 
                 style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }}>
            </div>
        </div>
    );
};

// --- UPDATED: CLIENTS SECTION (Minimalist Clean) ---
const TrustedPartners = () => {
  const logos = [
    { name: "Tencent", url: "https://upload.wikimedia.org/wikipedia/commons/6/6a/Tencent_logo_2017.svg" },
    { name: "Zynga", url: "https://cdn.worldvectorlogo.com/logos/zynga.svg" },
    { name: "Ubisoft", url: "https://cdn.worldvectorlogo.com/logos/ubisoft-4.svg" },
    { name: "EA", url: "https://cdn.worldvectorlogo.com/logos/electronic-arts-1.svg" },
    { name: "Unity", url: "https://cdn.worldvectorlogo.com/logos/unity-69.svg" },
    { name: "Unreal", url: "https://cdn.worldvectorlogo.com/logos/unreal-engine-1.svg" },
    { name: "Riot", url: "https://cdn.worldvectorlogo.com/logos/riot-games.svg" },
    { name: "Epic", url: "https://cdn.worldvectorlogo.com/logos/epic-games-2.svg" },
  ];

  const MarqueeTrack = () => (
    <div className="flex shrink-0 gap-24 animate-marquee items-center px-12">
        {logos.map((logo, i) => (
            <div key={i} className="flex items-center justify-center opacity-40 hover:opacity-100 transition-opacity duration-500">
               <img src={logo.url} 
                 alt={logo.name} 
                 className="h-8 md:h-10 w-auto object-contain brightness-0" loading="lazy" decoding="async" 
               />
            </div>
        ))}
    </div>
  );

  return (
    <div className="relative z-20 py-12 overflow-hidden bg-white/0 backdrop-blur-sm">
      <div className="flex overflow-hidden w-full mask-linear-fade">
        <MarqueeTrack />
        <MarqueeTrack />
      </div>
       <style jsx="true">{`
        .mask-linear-fade {
            mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
        }
      `}</style>
    </div>
  );
};

// --- 3D BACKGROUND ---
const CosmicField = () => {
  return (
    <group>
      <Sparkles count={80} scale={10} size={1} speed={0.4} opacity={0.6} color="#000000" />
      <Sparkles count={40} scale={15} size={2} speed={0.2} opacity={0.4} noise={0.2} color="#d4d4d8" />
    </group>
  );
};

// --- ANIMATION WRAPPERS ---
const Parallax = ({ children, offset = 50, className }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-offset, offset]);
  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y, willChange: "transform" }}>{children}</motion.div>
    </div>
  );
};

const RevealText = ({ children, delay = 0, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "110%" }}
        animate={isInView ? { y: 0 } : { y: "110%" }}
        transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1], delay }}
        style={{ willChange: "transform" }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// --- UI COMPONENTS ---
// --- UI COMPONENTS ---
const Preloader = ({ onComplete }) => {
  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
        <div className="relative">
            {/* Background/Outline Text */}
            <h1 
                className="text-[12vw] md:text-[8vw] font-bold font-lobster text-transparent tracking-wide select-none whitespace-nowrap"
                style={{ WebkitTextStroke: "1px #71717a" }} 
            >
                Lab Of Games
            </h1>
            
            {/* Filling Text (Masked) - Animated via ScaleX for GPU acceleration */}
            <h1 className="absolute top-0 left-0 text-[12vw] md:text-[8vw] font-bold font-lobster text-white tracking-wide select-none whitespace-nowrap overflow-hidden">
                <motion.span
                    className="block w-full"
                    initial={{ clipPath: "polygon(0 0, 0 0, 0 100%, 0% 100%)" }}
                    animate={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)" }}
                    transition={{ 
                        duration: 1.5, 
                        ease: "easeInOut",
                    }}
                    onAnimationComplete={() => {
                        setTimeout(onComplete, 200); 
                    }}
                >
                    Lab Of Games
                </motion.span>
            </h1>
        </div>
    </motion.div>
  );
};

// Old cursor removed

// --- ECOSYSTEM GRID (Apple-Style Bento) ---
const EcosystemItem = ({ title, subtitle, img, className, dark = false }) => (
    <div className={`relative group overflow-hidden rounded-[2rem] ${className} transition-transform duration-700 hover:scale-[1.01]`}>
        <div className={`absolute inset-0 ${dark ? 'bg-white' : 'bg-zinc-100'} z-0`}></div>
        
        {/* Image */}
        <img src={img} 
            alt={title} 
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-90" loading="lazy" decoding="async" 
        />
        
        {/* Content */}
        <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between z-10 bg-gradient-to-b from-white/0 via-white/0 to-white/60">
            <div>
                 <h3 className="text-base font-bold tracking-widest uppercase mb-2 text-black/80 drop-shadow-md">{subtitle}</h3>
                 <h2 className="text-lg md:text-4xl font-bold font-syne text-black leading-tight drop-shadow-lg">{title}</h2>
            </div>
            <div className="w-10 h-10 rounded-full bg-black/20 backdrop-blur-md flex items-center justify-center opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <ArrowRight size={20} className="text-black" />
            </div>
        </div>
    </div>
);

const EcosystemGrid = () => {
    return (
        <section className="py-32 px-6 bg-transparent relative z-10">
            <div className="max-w-[1200px] mx-auto">
                <div className="mb-20 text-center">
                    <h2 className="text-4xl md:text-7xl font-bold font-syne text-black tracking-tight mb-6">
                        Digital Ecosystems.
                    </h2>
                    <p className="text-lg md:text-2xl text-zinc-700 max-w-2xl mx-auto">
                        Seamlessly connected platforms designed to work as one.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-auto md:h-[800px]">
                    {/* Large Left */}
                    <EcosystemItem 
                        title="Immersive Worlds" 
                        subtitle="Core Technology"
                        img="https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?q=80&w=1974&auto=format&fit=crop"
                        className="md:row-span-2 h-[500px] md:h-full"
                    />
                    
                    {/* Right Stack */}
                    <div className="flex flex-col gap-6 h-full">
                        <EcosystemItem 
                             title="Interactive Commerce" 
                             subtitle="Platforms"
                             img="https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=2070&auto=format&fit=crop"
                             className="h-[300px] md:h-1/2"
                        />
                        <EcosystemItem 
                             title="Blockchain Integration" 
                             subtitle="Infrastructure"
                             img="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=2832&auto=format&fit=crop"
                             className="h-[300px] md:h-1/2"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- CANVAS SHOWCASE (Clean Parallax) ---
const CanvasProject = ({ title, category, img, index }) => {
    return (
        <div className="sticky top-0 h-screen w-full flex items-center justify-center bg-white overflow-hidden shadow-2xl">
            <div className="absolute inset-0">
                <img src={img} alt={title} className="w-full h-full object-cover opacity-50" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm"></div>
            </div>
            
            <div className="relative z-10 w-full max-w-[1400px] px-6 md:px-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                
                <div className="order-2 md:order-1">
                    <span className="block text-9xl font-bold text-zinc-200/50 mb-8 select-none">
                        0{index + 1}
                    </span>
                    <span className="inline-block px-4 py-2 rounded-full border border-black/20 text-sm font-mono tracking-widest text-black mb-6">
                        {category}
                    </span>
                    <h2 className="text-6xl md:text-8xl font-bold font-syne text-black tracking-tighter mb-10 leading-[0.9]">
                        {title}
                    </h2>
                    <button className="px-6 py-3 md:px-8 md:py-4 bg-black text-white rounded-full font-bold text-base tracking-widest uppercase hover:bg-zinc-200 transition-colors">
                        View Case Study
                    </button>
                </div>

                <div className="order-1 md:order-2 relative aspect-[4/5] md:aspect-square rounded-[3rem] overflow-hidden border border-black/10 shadow-2xl">
                     <img src={img} alt={title} loading="lazy" className="w-full h-full object-cover" />
                </div>
            </div>
        </div>
    );
};

const CanvasShowcase = () => {
    const works = [
        { title: "Velocity Legends", category: "Open World Racing", img: "https://cdn.akamai.steamstatic.com/steam/apps/1551360/capsule_616x353.webp" },
        { title: "Neon Drift", category: "Arcade Racer", img: "https://cdn.akamai.steamstatic.com/steam/apps/1222680/capsule_616x353.webp" },
        { title: "Apex Rivals", category: "Simulation", img: "https://cdn.akamai.steamstatic.com/steam/apps/1038250/capsule_616x353.webp" },
        { title: "Formula X", category: "Championship", img: "https://cdn.akamai.steamstatic.com/steam/apps/2108880/capsule_616x353.webp" },
    ];
    
    return (
        <section className="bg-transparent relative z-10">
             {works.map((work, i) => (
                <CanvasProject key={i} index={i} {...work} />
            ))}
        </section>
    );
};

// --- SERVICES COMPONENT ---
// --- REFACTORED: SERVICES STACK (Vertical Stacking) ---
const ServiceCard = ({ title, target, desc, img, icon: Icon, index }) => {
    return (
        <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="group relative rounded-[2rem] p-[1px] md:p-[2px] mb-10 md:mb-14 shadow-2xl"
        >
            {/* Static Outer Border (Hidden on hover) */}
            <div className="absolute inset-0 border border-black/10 rounded-[2rem] group-hover:opacity-0 transition-opacity duration-500 z-10 pointer-events-none"></div>

            {/* Rotating Edge Light Effect (Revealed on hover) */}
            <div className="absolute inset-0 overflow-hidden rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-0 pointer-events-none">
                <motion.div 
                    className="absolute top-1/2 left-1/2 w-[200%] h-[200%]"
                    style={{
                        background: 'conic-gradient(from 0deg, transparent 0 280deg, rgba(255,255,255,0.8) 360deg)',
                        x: "-50%",
                        y: "-50%",
                    }}
                    animate={{ rotate: [0, 360] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                />
            </div>
            
            {/* Inner Content Container */}
            <div 
                className="relative z-20 overflow-hidden bg-zinc-50 w-full h-full p-6 md:p-10 flex flex-col justify-end min-h-[400px]"
                style={{ borderRadius: 'calc(2rem - 2px)' }}
            >
                {/* Background Image & Gradient */}
                <div className="absolute inset-0 z-0">
                    <img src={img} alt={title} className="w-full h-full object-cover opacity-30 group-hover:opacity-60 group-hover:scale-[1.03] transition-all duration-700 ease-out" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent"></div>
                </div>

                {/* Text Content with Staggered Hover Animations */}
                <div className="relative z-10 max-w-2xl">
                    <h4 className="text-zinc-700 font-mono text-sm md:text-xs uppercase tracking-widest mb-3 transform group-hover:translate-x-2 transition-transform duration-500 ease-out">{target}</h4>
                    <h3 className="text-lg md:text-4xl font-bold font-syne text-black mb-4 drop-shadow-lg transform group-hover:translate-x-2 transition-transform duration-500 ease-out delay-75">{title}</h3>
                    <p className="text-zinc-800 text-base leading-relaxed mb-8 max-w-lg transform group-hover:translate-x-2 transition-transform duration-500 ease-out delay-100">{desc}</p>
                    
                    <div className="transform group-hover:translate-x-2 transition-transform duration-500 ease-out delay-150">
                        <button className="px-5 py-2.5 rounded-full bg-black text-white font-bold text-[15px] tracking-wide hover:bg-zinc-200 transition-colors inline-flex items-center gap-2 shadow-lg hover:shadow-xl">
                            Inquire Now <ArrowUpRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const CardStack = () => {
    const services = [
        {
            title: "Real Estate PropTech & Digital Twins",
            target: "For Builders & Developers",
            desc: "Interactive property walkthroughs, realtime architectural visualization, virtual tours, and digital twin platforms that help buyers explore, customize, and understand projects before construction even begins. What We Build: Interactive 3D walkthroughs, Virtual apartment tours, AR property visualization, Digital twin platforms, Touchscreen sales experiences, Unreal Engine visualization.",
            img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2075&auto=format&fit=crop",
            icon: Home,
        },
        {
            title: "Immersive AR/VR & Spatial Computing",
            target: "For Enterprises & Modern Brands",
            desc: "Immersive augmented and virtual reality systems built to train, simulate, and engage — designed for enterprise workflows, industrial use cases, education, and experiential brand marketing where physical presence isn't possible or practical. What We Build: VR training systems, AR product experiences, Meta Quest applications, Industrial & safety simulations, Interactive VR demos, Mixed reality experiences.",
            img: "https://images.unsplash.com/photo-1626379953822-baec19c3accd?q=80&w=2070&auto=format&fit=crop",
            icon: Glasses,
        },
        {
            title: "AI Platforms & Smart Applications",
            target: "For Startups & Enterprises",
            desc: "AI-powered applications and automation systems that improve workflows, engagement, analytics, and customer interaction. What We Build: AI workflow platforms, AI chat systems, Automation dashboards, Social automation tools, AI integrations, Intelligent SaaS products.",
            img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2070&auto=format&fit=crop",
            icon: Bot,
        },
        {
            title: "Game Development & Interactive Entertainment",
            target: "For Studios, Brands & IP Owners",
            desc: "End-to-end game development for studios, startups, and IP owners — single-player, multiplayer, casual, or competitive — using Unity and Unreal Engine, built for mobile, PC, and console. What We Build: Mobile & PC games, Multiplayer & live-service systems, Battle royale infrastructure, Casual & hyper-casual games, Cross-platform gameplay, Steam & app store launches.",
            img: "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?q=80&w=2070&auto=format&fit=crop",
            icon: Gamepad2,
        },
        {
            title: "Enterprise Interactive Systems",
            target: "For Enterprises, Events & Startups",
            desc: "Custom realtime software and interactive systems built specifically for enterprise use — kiosks, simulations, dashboards, and tools designed for internal operations, events, and customer-facing installations. What We Build: Interactive kiosks, Touchscreen systems, Simulation software, Realtime dashboards, WebGL applications, Custom enterprise tools.",
            img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=2070&auto=format&fit=crop",
            icon: Smartphone,
        },
        {
            title: "Automotive Experiences & In-Vehicle Software",
            target: "For Automotive Brands & OEMs",
            desc: "Realtime automotive technology spanning two fronts — customer-facing configurators and AR showroom experiences for automotive brands, and Android Automotive OS apps and games built for OEMs, fully compliant with in-vehicle infotainment standards. What We Build: Vehicle configurators, AR showroom experiences, Android Automotive OS apps, In-vehicle infotainment games, Car customization systems, Interactive launch experiences.",
            img: "https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?q=80&w=2069&auto=format&fit=crop",
            icon: Car,
        }
    ];

    return (
        <section className="py-24 md:py-32 px-6 md:px-20 relative bg-transparent z-10">
            <div className="max-w-[1300px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-24 items-start">
                
                {/* Sticky Left Column */}
                <div className="lg:w-1/3 lg:sticky lg:top-40 pt-10 z-20">
                    <RevealText>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black font-syne text-black tracking-tighter mb-6 leading-[1.1]">
                            What We Build <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-black to-zinc-600">For You.</span>
                        </h2>
                    </RevealText>
                    <RevealText delay={0.1}>
                        <p className="text-zinc-700 text-sm md:text-lg leading-relaxed max-w-sm">
                            We engineer immersive, high-performance digital realities tailored for modern enterprises, studios, and visionaries.
                        </p>
                    </RevealText>
                </div>
                
                {/* Scrolling Right Column */}
                <div className="lg:w-2/3 w-full">
                    {services.map((s, i) => (
                        <ServiceCard key={i} index={i} {...s} />
                    ))}
                </div>
            </div>
        </section>
    );
};



// --- CINEMATIC VIDEO (Apple Style) ---
const CinematicVideo = () => {
    const container = useRef(null);
    const { scrollYProgress } = useScroll({
        target: container,
        offset: ["start start", "end end"]
    });

    const textY = useTransform(scrollYProgress, [0, 0.4], [0, -100]);
    const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
    const videoScale = useTransform(scrollYProgress, [0, 0.4], [0.9, 1]); // Slight grow
    const videoRadius = useTransform(scrollYProgress, [0, 0.4], ["2rem", "0rem"]);
    
    return (
        <section ref={container} className="h-[200vh] relative z-10 bg-transparent">
            <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
                
                {/* Background Video */}
                <motion.div 
                    style={{ scale: videoScale, borderRadius: videoRadius }}
                    className="absolute inset-0 z-0 overflow-hidden w-full h-full"
                >
                     <div className="absolute inset-0 bg-white/40 z-10"></div>
                     <video 
                        className="w-full h-full object-cover" 
                        src="/hero-bg.mp4" 
                        autoPlay 
                        loop 
                        muted 
                        playsInline 
                     />
                </motion.div>

                {/* Floating Content */}
                <motion.div 
                    style={{ y: textY, opacity: textOpacity }}
                    className="relative z-20 text-center px-6"
                >
                    <span className="inline-block border border-black/30 bg-white/30 backdrop-blur-xl px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase text-black mb-6">
                        Showreel 2025
                    </span>
                    <h2 className="text-6xl md:text-9xl font-bold font-syne text-black tracking-tighter mb-6 drop-shadow-2xl">
                        Unimaginable <br/> Reality
                    </h2>
                    <div className="w-16 h-16 mx-auto rounded-full bg-black/20 backdrop-blur-md border border-black/30 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform hover:bg-black text-black hover:text-white">
                         <Play size={24} fill="currentColor" />
                    </div>
                </motion.div>
                
            </div>
        </section>
    );
};

// --- FEATURES ---
const FeatureCard = ({ title, desc, icon: Icon, img, delay, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay }}
      viewport={{ once: true }}
      className="group relative h-[400px] w-full overflow-hidden rounded-3xl bg-zinc-100 border border-zinc-800 cursor-pointer"
    >
      {/* Background Image with Zoom Effect */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white/60 z-10 group-hover:bg-white/40 transition-colors duration-500" />
        <img src={img} 
            alt={title} 
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 opacity-60 group-hover:opacity-100" loading="lazy" decoding="async"
        />
      </div>

      {/* Content Overlay */}
      <div className="relative z-20 flex flex-col justify-between h-full p-8">
        
        {/* Top: Icon & Index */}
        <div className="flex justify-between items-start">
             <div className="w-12 h-12 rounded-2xl bg-black/10 backdrop-blur-md border border-black/20 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                <Icon size={24} className="text-black group-hover:text-white transition-colors" />
             </div>
             <span className="text-base font-mono font-bold text-black/50 border border-black/10 px-3 py-1 rounded-full backdrop-blur-sm">
                SYS_0{index + 1}
             </span>
        </div>

        {/* Bottom: Text Info */}
        <div className="space-y-3 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
             <h3 className="text-lg md:text-4xl font-black font-syne text-black uppercase leading-none drop-shadow-lg">
                {title}
             </h3>
             <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-300">
                 <p className="text-base text-zinc-800 leading-relaxed pt-2 border-t border-black/20 mt-2">
                    {desc}
                 </p>
             </div>
        </div>
      </div>

      {/* Hover Borders/Glows */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-black/20 rounded-3xl transition-colors duration-300 pointer-events-none z-30"></div>
    </motion.div>
  );
};

const PremiumFeatures = () => {
  const features = [
    { 
        icon: CpuIcon, 
        title: "WebGL Core", 
        desc: "Native 3D environment rendering at locked 60FPS using hardware acceleration.",
        img: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1964&auto=format&fit=crop"
    },
    { 
        icon: Globe, 
        title: "Global Edge", 
        desc: "Distributed serverless architecture ensures <100ms latency worldwide.",
        img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop"
    },
    { 
        icon: Activity, 
        title: "Realtime Data", 
        desc: "Instantaneous data synchronization across all connected clients.",
        img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
    },
    { 
        icon: Shield, 
        title: "Fortified Nexus", 
        desc: "Enterprise-grade encryption with automated threat detection systems.",
        img: "https://images.unsplash.com/photo-1563089145-599997674d42?q=80&w=2070&auto=format&fit=crop"
    },
    { 
        icon: Smartphone, 
        title: "Adaptive Viewport", 
        desc: "Liquid layouts that maintain visual integrity across 8k monitors to mobile screens.",
        img: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop"
    },
    { 
        icon: BarChart3, 
        title: "Neural Analytics", 
        desc: "AI-driven user behavior tracking to optimize conversion paths in real-time.",
        img: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=1965&auto=format&fit=crop"
    },
  ];

  return (
    <section className="relative py-32 px-6 md:px-20 bg-transparent z-10 overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-zinc-100/20 blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        <div className="mb-24 flex flex-col md:flex-row md:items-end justify-between gap-6 px-4">
          <div>
            <RevealText>
              <h2 className="text-6xl md:text-8xl font-black font-syne text-black tracking-tighter mb-6">
                SYSTEM <span className="text-transparent bg-clip-text bg-gradient-to-r from-zinc-600 to-black">MODULES</span>
              </h2>
            </RevealText>
            <RevealText delay={0.1}>
              <p className="text-zinc-700 max-w-xl text-lg md:text-2xl leading-relaxed">
                Our architecture is built on three pillars: Speed, Immersion, and Security. Experience the future of web performance.
              </p>
            </RevealText>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, i) => <FeatureCard key={i} index={i} {...feature} delay={i * 0.1} />)}
        </div>
      </div>
    </section>
  );
};




// --- GAMES SHOWCASE (Apple Arcade Style) ---
const GameCard = ({ title, category, img }) => (
    <div className="relative shrink-0 w-[300px] md:w-[400px] aspect-video rounded-2xl overflow-hidden group cursor-pointer border border-black/10 hover:border-black/30 transition-colors">
        <img 
            src={img} 
            alt={title} 
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-white/90 to-transparent"></div>
        <div className="absolute bottom-0 left-0 p-6">
            <p className="text-sm font-bold text-black/60 mb-1 uppercase tracking-wider">{category}</p>
            <h3 className="text-2xl md:text-2xl font-bold font-syne text-black leading-none">{title}</h3>
        </div>
    </div>
);

const GameRow = ({ games, direction = "left", speed = 20 }) => (
    <div className="flex gap-6 overflow-hidden py-4">
        <div 
            className={`flex gap-6 shrink-0 ${direction === "left" ? "animate-marquee" : "animate-marquee-reverse"}`}
            style={{ animationDuration: `${speed}s` }}
        >
            {games.map((game, i) => <GameCard key={i} {...game} />)}
            {games.map((game, i) => <GameCard key={`duplicate-${i}`} {...game} />)}
        </div>
        <div 
            className={`flex gap-6 shrink-0 ${direction === "left" ? "animate-marquee" : "animate-marquee-reverse"}`}
            style={{ animationDuration: `${speed}s` }}
        >
            {games.map((game, i) => <GameCard key={`duplicate-2-${i}`} {...game} />)}
            {games.map((game, i) => <GameCard key={`duplicate-3-${i}`} {...game} />)}
        </div>
    </div>
);

const GamesShowcase = () => {
    const row1 = [
        { title: "Oceanhorn 2", category: "Adventure", img: "https://cdn.akamai.steamstatic.com/steam/apps/1393750/capsule_616x353.webp" },
        { title: "Sneaky Sasquatch", category: "Arcade", img: "https://images.igdb.com/igdb/image/upload/t_720p/co238m.webp" },
        { title: "Sayonara Wild Hearts", category: "Music", img: "https://cdn.akamai.steamstatic.com/steam/apps/1122720/capsule_616x353.webp" },
        { title: "Mini Motorways", category: "Strategy", img: "https://cdn.akamai.steamstatic.com/steam/apps/1127500/capsule_616x353.webp" },
    ];
    
    const row2 = [
         { title: "Alto's Odyssey", category: "Endless", img: "https://cdn.akamai.steamstatic.com/steam/apps/1233280/capsule_616x353.webp" },
         { title: "Monument Valley", category: "Puzzle", img: "https://cdn.akamai.steamstatic.com/steam/apps/1927720/capsule_616x353.webp" },
         { title: "Gris", category: "Platformer", img: "https://cdn.akamai.steamstatic.com/steam/apps/683320/capsule_616x353.webp" },
         { title: "Stardew Valley", category: "RPG", img: "https://cdn.akamai.steamstatic.com/steam/apps/413150/capsule_616x353.webp" },
    ];

    const row3 = [
        { title: "What the Golf?", category: "Sports", img: "https://cdn.akamai.steamstatic.com/steam/apps/783960/capsule_616x353.webp" },
        { title: "Grindstone", category: "Puzzle", img: "https://cdn.akamai.steamstatic.com/steam/apps/1334280/capsule_616x353.webp" },
        { title: "Sky: Children", category: "Social", img: "https://cdn.akamai.steamstatic.com/steam/apps/2325290/capsule_616x353.webp" },
        { title: "The Pathless", category: "Action", img: "https://cdn.akamai.steamstatic.com/steam/apps/1800910/capsule_616x353.webp" },
    ];

    return (
        <section className="py-24 bg-transparent overflow-hidden relative z-10">
            <div className="mb-16 px-6 md:px-20">
                <h2 className="text-4xl md:text-6xl font-black font-syne text-black mb-6">
                    Made with <MetallicText>Passion</MetallicText>
                </h2>
                <p className="text-lg md:text-xl text-zinc-700 max-w-xl">
                    A collection of immersive titles powering the next generation of gaming.
                </p>
            </div>
            
            <div className="space-y-4">
               <GameRow games={row1} direction="left" speed={40} />
               <GameRow games={row2} direction="right" speed={50} />
               <GameRow games={row3} direction="left" speed={45} />
            </div>
        </section>
    );
};

// --- NEW BEACON.LI STYLE SECTIONS WITH ORIGINAL CONTENT ---

const ProblemStatement = () => (
    <section className="py-24 px-4 md:px-20 bg-white flex flex-col items-center justify-center">
        <div className="max-w-[1000px] w-full mx-auto text-center relative z-10 mb-8 md:mb-16">
            <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 border border-zinc-200 bg-zinc-50 text-zinc-600 rounded-full text-[10px] sm:text-xs md:text-sm font-sans font-medium mb-6 shadow-sm whitespace-nowrap">
                  The cost of relying on standard presentations
              </div>
            
            <RevealText>
                <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-6xl font-['Playfair_Display'] font-normal leading-[1.2] tracking-tight mb-0 md:mb-6 text-black">
                    Experience It <br />
                        <span className="italic text-zinc-600">To Fully Believe It.</span>
                </h2>
            </RevealText>
            <p className="mt-4 md:mt-6 text-zinc-600 text-sm md:text-lg px-0">Slide decks, 2D renders, and flat presentations fail to capture attention or drive deep engagement.</p>
        </div>
        
        <div className="max-w-[1200px] w-full mx-auto bg-[#0a0a0a] rounded-[2.5rem] p-4 md:p-8 flex flex-col md:flex-row gap-8 items-stretch shadow-2xl">
            {/* Left side: The Old Way vs New Way */}
            <div className="flex-1 text-white p-4 md:p-8 flex flex-col justify-center">
                <h3 className="text-lg md:text-4xl font-bold font-sans tracking-tight mb-10">The Engagement Gap:</h3>
                
                <div className="space-y-10">
                    <div className="flex gap-6 items-start">
                        <div className="w-10 h-10 shrink-0"><Home className="w-full h-full text-white/50" strokeWidth={1.5} /></div>
                        <div>
                            <p className="hidden md:block text-white/60 text-xs md:text-base mb-1">Real Estate</p>
                            <p className="text-sm md:text-xl font-medium text-white/80">Buyers struggle to connect with floor plans and unfinished spaces.</p>
                        </div>
                    </div>
                    
                    <div className="flex gap-6 items-start">
                        <div className="w-10 h-10 shrink-0"><Car className="w-full h-full text-white/50" strokeWidth={1.5} /></div>
                        <div>
                            <p className="text-white/60 text-xs md:text-base mb-1">Automotive</p>
                            <p className="text-sm md:text-xl font-medium text-white/80">Brands can’t always showcase every variant and experience effectively.</p>
                        </div>
                    </div>
                    
                    <div className="flex gap-6 items-start">
                        <div className="w-10 h-10 shrink-0"><Gamepad2 className="w-full h-full text-white/50" strokeWidth={1.5} /></div>
                        <div>
                            <p className="text-white/60 text-xs md:text-base mb-1">Gaming & AI</p>
                            <p className="text-sm md:text-xl font-medium text-white/80">Products often fail when they lack immersion, clarity, or real user engagement.</p>
                        </div>
                    </div>
                    
                    <div className="flex gap-6 items-start">
                        <div className="w-10 h-10 shrink-0"><Building2 className="w-full h-full text-white/50" strokeWidth={1.5} /></div>
                        <div>
                            <p className="text-white/60 text-xs md:text-base mb-1">Enterprises</p>
                            <p className="text-sm md:text-xl font-medium text-white/80">Continue relying on static presentations and costly prototypes that don’t create impact.</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Right side: Cinematic Image */}
            <div className="flex-[1.2] rounded-3xl overflow-hidden min-h-[400px] relative">
                <img src="/ruins_lion.webp" alt="Cute Lion Mascot in Ancient Ruins" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
        </div>
        
        <p className="mt-12 text-center text-sm md:text-2xl font-medium text-black max-w-4xl mx-auto leading-relaxed">
            Modern customers expect more than visuals — they expect interactive, intelligent, and immersive experiences.<br/>
            <span className="text-zinc-500">Lab of Games helps brands bridge that gap through AI-powered platforms, immersive AR/VR applications, games, automotive visualizations, and real-time 3D experiences that bring ideas to life.</span>
        </p>
    </section>
);

// --- CORE SERVICES SECTION ---
const coreServices = [
    {
        title: 'Game Development & Interactive Entertainment',
        target: 'For Studios, Brands & IP Owners',
        desc: 'End-to-end game development for studios, startups, and IP owners — single-player, multiplayer, casual, or competitive — using Unity and Unreal Engine, built for mobile, PC, and console.',
        builds: ['Mobile & PC games', 'Multiplayer & live-service systems', 'Battle royale infrastructure', 'Casual & hyper-casual games', 'Cross-platform gameplay', 'Steam & app store launches'],
        bestFor: 'Unity, Unreal Engine, Photon Fusion, PlayFab, Mirror Networking.',
        img: '/gamedev-home.webp',
        icon: Gamepad2,
        link: '/game-development'
    },
    {
        title: 'Immersive AR/VR & Spatial Computing',
        target: 'For Enterprises & Modern Brands',
        desc: 'Immersive augmented and virtual reality systems built to train, simulate, and engage — designed for enterprise workflows, industrial use cases, education, and experiential brand marketing where physical presence isn\'t possible or practical.',
        builds: ['VR training systems', 'AR product experiences', 'Meta Quest applications', 'Industrial & safety simulations', 'Interactive VR demos', 'Mixed reality experiences'],
        bestFor: 'Meta Quest, Vision Pro, Android, iOS, WebXR, HTC Vive.',
        img: '/arVr-home.webp',
        icon: Glasses,
        link: '/ar-vr'
    },
    {
        title: 'AI Platforms & Smart Applications',
        target: 'For Startups & Enterprises',
        desc: 'AI-powered applications and automation systems that improve workflows, engagement, analytics, and customer interaction.',
        builds: ['AI workflow platforms', 'AI chat systems', 'Automation dashboards', 'Social automation tools', 'AI integrations', 'Intelligent SaaS products'],
        bestFor: 'Node.js, Python, OpenAI APIs, workflow automation systems, cloud infrastructure.',
        img: '/ai-home.webp',
        icon: Bot,
        link: '/ai-platforms'
    },
    {
        title: 'Real Estate PropTech & Digital Twins',
        target: 'For Builders & Developers',
        desc: 'Interactive property walkthroughs, realtime architectural visualization, virtual tours, and digital twin platforms that help buyers explore, customize, and understand projects before construction even begins.',
        builds: ['Interactive 3D walkthroughs', 'Virtual apartment tours', 'AR property visualization', 'Digital twin platforms', 'Touchscreen sales experiences', 'Unreal Engine visualization'],
        bestFor: 'Real estate developers, architects, builders, and smart-city projects.',
        img: '/realEstate-home.webp',
        icon: Home,
        link: '/real-estate'
    },
    {
        title: 'Enterprise Interactive Systems',
        target: 'For Enterprises, Events & Startups',
        desc: 'Custom realtime software and interactive systems built specifically for enterprise use — kiosks, simulations, dashboards, and tools designed for internal operations, events, and customer-facing installations.',
        builds: ['Interactive kiosks', 'Touchscreen systems', 'Simulation software', 'Realtime dashboards', 'WebGL applications', 'Custom enterprise tools'],
        bestFor: 'Enterprises, events, startups, and product-focused businesses.',
        img: '/enterprise-home.webp',
        icon: Code2,
        link: '/enterprise-interactive'
    },
    {
        title: 'Automotive Experiences & In-Vehicle Software',
        target: 'For Automotive Brands & OEMs',
        desc: 'Realtime automotive technology spanning two fronts — customer-facing configurators and AR showroom experiences for automotive brands, and Android Automotive OS apps and games built for OEMs, fully compliant with in-vehicle infotainment standards.',
        builds: ['Vehicle configurators', 'AR showroom experiences', 'Android Automotive OS apps', 'In-vehicle infotainment games', 'Car customization systems', 'Interactive launch experiences'],
        bestFor: 'Automotive brands, dealerships, startups, and mobility companies.',
        img: '/automotive-home.webp',
        icon: Car,
        link: '/automotive'
    },
];

const CoreServices = () => (
    <section className="py-24 md:py-32 px-6 md:px-12 flex flex-col items-center relative overflow-hidden" 
             style={{ background: 'linear-gradient(180deg, #000000 0%, #000000 100%)' }}>
        
        {/* Header */}
        <div className="text-center mb-24 max-w-3xl mx-auto relative z-10">
            <RevealText>
                <h2 className="text-4xl sm:text-4xl md:text-6xl lg:text-6xl font-['Playfair_Display'] font-normal leading-[1.2] tracking-tight mb-6 text-white">
                    Our Core <span className="italic">Services</span>
                </h2>
                <p className="text-white/60 text-sm md:text-xl font-light">
                    Every service is built to engage, convert, and scale—from visualizing concepts to deploying multiplayer infrastructure.
                </p>
            </RevealText>
        </div>

        <div className="max-w-[1200px] w-full relative z-10 space-y-12 md:space-y-48">
            {coreServices.map((service, i) => {
                const Icon = service.icon;
                const isEven = i % 2 === 0;
                return (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        viewport={{ once: true, margin: '-100px' }}
                        className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                    >
                        {/* Image side with glowing background and black box */}
                        <div className="w-full lg:w-1/2 relative group px-0 sm:px-6">
                            
                            {/* Outer Black Box (Device frame) with exact hugging glow */}
                            <div 
                                className="relative p-2 sm:p-3 rounded-[1.5rem] sm:rounded-[2rem] bg-[#070707] border border-white/10 z-10 transition-shadow duration-700 ease-out"
                                style={{ 
                                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)'
                                }}
                            >
                                
                                {/* Inner Screen */}
                                <div className="relative rounded-[1.2rem] sm:rounded-[1.6rem] overflow-hidden border border-white/5 bg-[#0a0a0a]">
                                    


                                    <img src={service.img}
                                        alt={service.title}
                                        className="w-full aspect-square object-cover opacity-100" loading="lazy" decoding="async"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Text side */}
                        <div className="w-full lg:w-1/2 px-0 sm:px-6 lg:px-0 lg:pl-6">
                            <h3 className="text-lg md:text-4xl font-bold text-white mb-6 leading-tight tracking-tight">
                                {service.title}
                            </h3>
                            <p className="text-white/70 text-sm md:text-lg leading-relaxed mb-8">
                                {service.desc}
                            </p>

                            <div className="space-y-4 mb-8">
                                {service.builds.map((item, j) => (
                                    <div key={j} className="flex items-center gap-4">
                                        <div className="w-1.5 h-1.5 rounded-full bg-white shrink-0" />
                                        <span className="text-sm md:text-lg text-white/95 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Target badge & Know More Button */}
                            <div className="flex flex-wrap items-center gap-4 mt-2">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00ffcc]/10 border border-[#00ffcc]/20 text-white text-[14px] md:text-[14px] font-semibold uppercase tracking-widest">
                                    <Icon size={14} />
                                    {service.target}
                                </div>
                                <Link to={service.link}>
                                    <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-black/40 backdrop-blur-md border border-[#00ffcc]/30 text-white text-sm font-bold tracking-wide uppercase hover:bg-[#00ffcc] hover:text-black transition-all duration-300 shadow-[0_0_15px_rgba(0,255,204,0.1)] hover:shadow-[0_0_20px_rgba(0,255,204,0.3)]">
                                        Know More <ArrowUpRight size={14} />
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                );
            })}
        </div>

        <p className="mt-28 text-center text-[16px] md:text-xl text-white/60 max-w-xl mx-auto relative z-10">
            Not sure which service fits your project?{' '}
            <a href="https://wa.me/917973152831" target="_blank" rel="noopener noreferrer"
                className="text-white hover:text-white transition-colors font-medium inline-flex items-center gap-1.5 ml-1">
                Talk to us <ArrowRight size={14} />
            </a>
        </p>
    </section>
);



const WorkflowProcess = () => {
    const steps = [
        { title: "Discover", desc: "We understand your vision, challenges, and business goals.", img: "/step-1.webp" },
        { title: "Plan", desc: "We define the strategy, scope, timeline, and technical roadmap.", img: "/step-2.webp" },
        { title: "Create", desc: "Our team designs, develops, and iterates with transparent collaboration.", img: "/step-3.webp" },
        { title: "Launch", desc: "We deliver, deploy, and support your product for long-term success.", img: "/step-4.webp" }
    ];
    return (
        <section className="pt-24 pb-8 md:pb-12 px-4 md:px-20 bg-white flex flex-col items-center">
            <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-6xl font-['Playfair_Display'] font-normal leading-[1.2] tracking-tight mb-6 text-black">
                    Turning Ideas Into <br/> <span className="italic">Scalable Experiences</span>
                </h2>
                <p className="text-zinc-600 text-sm md:text-lg max-w-2xl mx-auto">Fast onboarding. Transparent communication. Built for modern businesses.</p>
            </div>
            <div className="max-w-[1400px] w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {steps.map((step, i) => (
                    <div key={i} className="rounded-3xl bg-[#0a0a0a] overflow-hidden flex flex-col shadow-xl group border border-zinc-800">
                        <div className="h-48 w-full overflow-hidden">
                            <img src={step.img} alt={step.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
                        </div>
                        <div className="p-8 flex flex-col flex-1 relative">
                            <div className="absolute top-0 right-8 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center font-bold text-black font-sans shadow-lg">0{i + 1}</div>
                            <h3 className="text-xl md:text-2xl font-bold font-sans text-white mb-3 mt-2">{step.title}</h3>
                            <p className="text-white/60 text-base leading-relaxed">{step.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const FeaturedWork = () => {
    const [projects, setProjects] = useState([]);
    
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/projects`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    const featured = data.filter(p => p.isFeatured && p.status === 'published');
                    if (featured.length === 3) {
                        setProjects(featured);
                    } else {
                        setProjects([]);
                    }
                } else {
                    console.error("API did not return an array of projects:", data);
                    setProjects([]);
                }
            })
            .catch(err => console.error("Error fetching projects:", err));
    }, []);

    if (projects.length !== 3) return null;

    return (
        <section className="py-24 px-6 md:px-20 bg-[#02100a] flex flex-col items-center">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-4xl sm:text-4xl md:text-6xl lg:text-6xl font-['Playfair_Display'] font-normal leading-[1.2] tracking-tight mb-6 text-white">
                    A Few Things <span className="italic">We've Built</span>
                </h2>
                <p className="text-white/70 text-base md:text-lg">Delivering impact across industries.</p>
            </div>
            
            <div className="max-w-[1400px] w-full bg-[#0a4d33] rounded-[3rem] p-4 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 shadow-[0_0_100px_rgba(10,77,51,0.4)]">
                {projects.map((p, i) => (
                    <div key={i} className="flex flex-col h-full group bg-[#0d593b] rounded-[2.5rem] overflow-hidden shadow-lg border border-white/5">
                        <div className="aspect-[4/3] w-full overflow-hidden">
                            <img src={p.coverImage || '/robot_projects.webp'} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="p-8 flex flex-col flex-1">
                            <h4 className="text-sm font-mono text-white uppercase tracking-widest mb-3">{p.category}</h4>
                            <h3 className="text-xl md:text-3xl font-bold font-sans text-white mb-4">{p.title}</h3>
                            <p className="text-white/80 text-base leading-relaxed flex-1 mb-8">{p.shortSummary || (p.content ? p.content.replace(/<[^>]*>?/gm, '').substring(0, 100) : '')}</p>
                            <Link to={`/portfolio/${p.slug}`} className="self-start px-5 py-2.5 rounded-full bg-black/30 text-white text-base font-semibold flex items-center gap-2 hover:bg-black/50 transition-colors">
                                View Case Study <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};















const StatsSection = () => {
    const stats = [
        { number: "600+", label: "Projects Delivered" },
        { number: "100M+", label: "App Downloads" },
        { number: "8+", label: "Years Innovating" },
        { number: "12+", label: "Countries Served" }
    ];

    return (
        <section className="pt-4 pb-20 md:pb-24 bg-white relative">
            <div className="max-w-[1200px] mx-auto px-6">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16 bg-[#0a0a0a] rounded-[2rem] p-8 md:p-12 border border-zinc-800 shadow-2xl">
                    
                    {/* Left: Simple Heading */}
                    <div className="lg:w-1/3 text-center lg:text-left flex flex-col justify-center">
                        <motion.h2 
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-3xl md:text-4xl font-['Playfair_Display'] font-normal text-white mb-3"
                        >
                            Global <span className="font-lobster text-zinc-400">Impact</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-zinc-400 text-sm md:text-base font-light max-w-sm mx-auto lg:mx-0 leading-relaxed"
                        >
                            Scalable digital experiences deployed worldwide. We engineer realities at scale.
                        </motion.p>
                    </div>

                    {/* Right: Small Type Modern Stats */}
                    <div className="lg:w-2/3 w-full grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 lg:pl-10 lg:border-l lg:border-zinc-800">
                        {stats.map((stat, i) => (
                            <motion.div 
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="flex flex-col items-center lg:items-start text-center lg:text-left group"
                            >
                                <h3 className="text-3xl md:text-[40px] font-bold text-white mb-2 tracking-tight group-hover:text-white transition-colors duration-300">
                                    {stat.number}
                                </h3>
                                <p className="text-zinc-500 text-xs font-semibold tracking-wider uppercase group-hover:text-white transition-colors duration-300">
                                    {stat.label}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
};













const TrustSection = () => {
    const reasons = [
        {
            title: "Proven Delivery Results",
            desc: "Your clients aren't looking for another delayed project—they want results they can measure. Our battle-tested pipelines allow you to show real progress fast, turning ambitious ideas into polished playable builds in weeks.",
            icon: BarChart3,
            image: "/tech-1.webp"
        },
        {
            title: "Production-Ready Engineering",
            desc: "Skip the technical debt nightmare. Labofgames' custom architectures sit perfectly alongside your game or app, delivering robust multiplayer and frontend automation without bottlenecking your core logic.",
            icon: CpuIcon,
            image: "/tech-2.webp"
        },
        {
            title: "Compelling Project Economics",
            desc: "By accelerating development cycles by up to 75%, we get your product to market significantly faster. Our focus on optimization ensures seamless cross-platform performance, while delivering massive long-term cost savings.",
            icon: Layers,
            image: "/tech-3.webp"
        }
    ];

    return (
        <section className="pt-12 pb-24 md:pt-16 md:pb-32 bg-white relative">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                
                {/* Clean, Dark Heading on White Background */}
                <div className="text-center mb-20 md:mb-28 flex flex-col items-center">
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-['Playfair_Display'] text-gray-900 leading-tight mb-6">
                        Why Studios Trust <br className="hidden md:block" />
                        <span className="font-lobster text-black">Lab Of Games</span>
                    </h2>
                    <p className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed">
                        We deliver the engineering scale and technical precision required for world-class interactive entertainment.
                    </p>
                </div>

                {/* Simple & Nice 3-Column Image Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
                    {reasons.map((reason, i) => {
                        const Icon = reason.icon;
                        
                        return (
                            <motion.div 
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.15 }}
                                className="group flex flex-col"
                            >
                                {/* Clean Image Container */}
                                <div className="relative w-full h-[280px] lg:h-[340px] mb-10">
                                    {/* Image Wrapper */}
                                    <div className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-lg group-hover:shadow-2xl transition-shadow duration-500 border border-gray-100">
                                        <img src={reason.image} 
                                            alt={reason.title} 
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.5s] ease-out" loading="lazy" decoding="async"
                                        />
                                    </div>
                                    
                                    {/* Icon Floating over image */}
                                    <div className="absolute -bottom-6 left-8 w-16 h-16 rounded-2xl bg-white border border-gray-100 shadow-xl flex items-center justify-center z-10 group-hover:-translate-y-3 group-hover:shadow-2xl group-hover:border-[#0a4d33]/20 transition-all duration-500">
                                        <Icon className="w-8 h-8 text-gray-700 group-hover:text-[#0a4d33] transition-colors duration-500" strokeWidth={1.5} />
                                    </div>
                                </div>
                                
                                {/* Content */}
                                <div className="pl-2">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4 tracking-tight group-hover:text-[#0a4d33] transition-colors duration-300">
                                        {reason.title}
                                    </h3>
                                    <p className="text-gray-600 text-base leading-relaxed font-light">
                                        {reason.desc}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};



const LeadCapture = () => {
    return (
        <section id="contact" className="py-24 md:py-32 px-4 relative overflow-hidden bg-white">
            {/* Grid Background Image */}
            <div className="absolute inset-0 z-0 opacity-40 pointer-events-none hidden md:block">
                <img src="/grid.webp" alt="Grid Background" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            {/* Architecture Background (Mobile Only) */}
            <div className="absolute inset-0 z-0 block md:hidden">
                <img src="/3d-artist.webp" alt="Background" className="w-full h-full object-cover opacity-100" loading="lazy" decoding="async" />
            </div>
  
            <div className="relative z-10 max-w-[1200px] mx-auto flex flex-col items-center">
                {/* Top Pill */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="hidden md:inline-flex items-center justify-center text-center bg-[#09472d] text-white border-2 border-[#116e45] rounded-full px-6 py-2.5 text-base font-bold shadow-lg mb-8"
                >
                    Free consultation. No commitment. Response within 24 hours.
                </motion.div>

                {/* Main Banner */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="relative w-full rounded-[1.5rem] md:rounded-[3.5rem] shadow-2xl bg-black border border-[#333] md:border-[4px] md:border-white"
                >
                    {/* Mobile Pill on Top Border */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 md:hidden flex items-center justify-center bg-[#09472d] text-white border-2 border-[#116e45] rounded-full px-4 py-1.5 text-base font-bold shadow-lg whitespace-nowrap">
                        Get Response Within 1 Hrs
                    </div>

                    {/* Background Image */}
                    <div className="absolute inset-0 hidden md:block rounded-[1.5rem] md:rounded-[3.5rem] overflow-hidden">
                        <img src="/3d-artist.webp" alt="Background" className="w-full h-full object-cover opacity-100" loading="lazy" decoding="async" />
                    </div>

                    <div className="relative z-10 px-8 py-8 sm:px-10 md:p-10 flex flex-col items-center text-center mt-4">
                        <h2 className="text-2xl sm:text-2xl md:text-[40px] lg:text-[48px] font-['Playfair_Display'] font-normal leading-[1.2] tracking-tight text-white mb-6">
                            Let's Build <span className="italic text-white">Something Together</span>
                        </h2>
                        <p className="hidden md:block text-white/80 text-base md:text-base leading-relaxed font-sans font-medium mb-3 max-w-2xl drop-shadow-md">
                            Reach out with your project details, and we'll reply within 24 hours with honest advice.</p>
                        
                        {/* Embedded Form inside CTA */}
                        <ContactForm />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const FoundationFooter = () => {
    return (
        <footer className="bg-[#050505] pt-24 pb-12 flex flex-col relative z-20 font-sans text-white border-t border-white/10">
            <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12">
                
                {/* Main Footer Content */}
                <div className="flex flex-col lg:flex-row justify-between items-start gap-16 mb-20">
                    
                    {/* Brand Section */}
                    <div className="flex flex-col items-start max-w-sm">
                        <h2 className="text-3xl md:text-5xl font-normal font-lobster text-white tracking-wider mb-6">
                            Lab Of Games
                        </h2>
                        <p className="text-zinc-400 text-base leading-relaxed mb-8">
                            We build next-generation interactive experiences, AI platforms, and spatial computing solutions for forward-thinking enterprises globally.
                        </p>
                        
                    </div>

                    {/* Links Section (3 Columns) */}
                    <div className="flex-1 w-full grid grid-cols-2 md:grid-cols-3 gap-10">
                        {/* Column 1: Company */}
                        <div className="flex flex-col">
                            <h3 className="text-white font-bold text-base tracking-widest uppercase mb-8">Company</h3>
                            <ul className="space-y-4">
                                <li><Link to="/about" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">About Us</Link></li>
                                <li><Link to="/portfolio" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Our Work</Link></li>
                                <li><a href="#" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Careers</a></li>
                                <li><a href="#" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Contact Us</a></li>
                            </ul>
                        </div>

                        {/* Column 2: Services */}
                        <div className="flex flex-col">
                            <h3 className="text-white font-bold text-base tracking-widest uppercase mb-8">Services</h3>
                            <ul className="space-y-4">
                                <li><Link to="/ai-platforms" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">AI Platforms</Link></li>
                                <li><Link to="/enterprise-interactive" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Real-Time 3D</Link></li>
                                <li><Link to="/ar-vr" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">AR/VR Apps</Link></li>
                                <li><Link to="/game-development" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Game Development</Link></li>
                            </ul>
                        </div>

                        {/* Column 3: Industries & Legal */}
                        <div className="flex flex-col">
                            <h3 className="text-white font-bold text-base tracking-widest uppercase mb-8">Industries & Legal</h3>
                            <ul className="space-y-4">
                                <li><Link to="/real-estate" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Real Estate</Link></li>
                                <li><Link to="/automotive" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Automotive</Link></li>
                                <li><Link to="/privacy-policy" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Privacy Policy</Link></li>
                                <li><Link to="/terms-of-service" className="text-zinc-400 hover:text-white text-sm md:text-base transition-colors">Terms of Service</Link></li>
                            </ul>
                        </div>
                    </div>

                </div>

                {/* Divider */}
                <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-12"></div>



                {/* Bottom Bar */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-zinc-600">
                    <p>&copy; {new Date().getFullYear()} Lab Of Games. All rights reserved.</p>
                    <div className="flex items-center gap-6 mt-0.5">
                        <a href="https://www.linkedin.com/company/lab-of-games/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-[#0077b5] hover:border-[#0077b5] transition-all bg-zinc-900/50">
                            <Linkedin className="w-4 h-4" />
                        </a>
                        <div className="flex items-center gap-2">
                            <PhoneCall className="w-[14px] h-[14px] text-zinc-400 shrink-0" />
                            <span className="text-zinc-300 text-[15px]">+91 7973152831</span>
                        </div>
                    </div>
                </div>

            </div>
        </footer>
    );
};

// --- FAQ SECTION ---
const FAQSection = () => {
    const faqs = [
        {
            question: "What industries do you specialize in?",
            answer: "We build interactive experiences primarily for Real Estate, Automotive, Gaming, Healthcare, and Enterprise sectors, though our scalable technology can be adapted to almost any industry needing 3D, AR/VR, or AI solutions."
        },
        {
            question: "How is Lab of Games different from a traditional development agency?",
            answer: "We engineer immersive digital realities rather than just writing code. By leveraging game-engine technology (Unity/Unreal) and realtime 3D alongside traditional scalable backends, we deliver highly interactive products that standard web agencies cannot."
        },
        {
            question: "Do you handle both design and development?",
            answer: "Yes. We offer end-to-end production, including 3D art creation, UI/UX design, frontend and backend development, and scalable cloud deployment. You work directly with our creators without middle layers."
        },
        {
            question: "What platforms do your experiences support?",
            answer: "We build cross-platform solutions that run seamlessly on mobile, desktop, WebGL (browsers), and AR/VR headsets like Meta Quest and Apple Vision Pro."
        },
        {
            question: "How do we get started on a project?",
            answer: "You can start by booking a free consultation or reaching out to us via WhatsApp. We will discuss your vision, define the scope and technical roadmap, and provide a clear timeline for delivery."
        }
    ];

    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section className="py-24 px-6 md:px-20 bg-black flex flex-col items-center">
            <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-4xl md:text-6xl lg:text-6xl font-['Playfair_Display'] font-normal leading-[1.2] tracking-tight mb-6 text-white">
                    Frequently Asked <span className="italic">Questions</span>
                </h2>
            </div>
            <div className="w-full max-w-[900px] bg-[#111111] p-3 md:p-4 rounded-3xl md:rounded-[2.5rem] flex flex-col gap-2 shadow-2xl">
                {faqs.map((faq, index) => {
                    const isOpen = index === openIndex;
                    return (
                        <div 
                            key={index} 
                            onClick={() => setOpenIndex(isOpen ? -1 : index)}
                            className="bg-[#050505] rounded-2xl md:rounded-[1.5rem] p-5 md:p-6 cursor-pointer border border-white/5 transition-all duration-300 hover:border-white/10"
                        >
                            <div className="flex justify-between items-center gap-4">
                                <h3 className="text-white text-[16px] md:text-lg font-medium tracking-wide">{faq.question}</h3>
                                <div className="shrink-0 w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center transition-colors hover:bg-zinc-700">
                                    {isOpen ? <Minus size={16} className="text-white" /> : <Plus size={16} className="text-white" />}
                                </div>
                            </div>
                            <AnimatePresence>
                                {isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                                        animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="overflow-hidden"
                                    >
                                        <p className="text-zinc-400 text-[14px] md:text-base leading-relaxed pr-8 md:pr-12 font-light">
                                            {faq.answer}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// --- HERO VIDEO (deferred load so 54MB doesn't block page render) ---
const HeroVideo = () => {
    const videoRef = useRef(null);
    const [videoLoaded, setVideoLoaded] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Start loading video after a short delay so page content renders first
        const timer = setTimeout(() => {
            video.src = '/hero-bg.mp4';
            video.load();
            video.play().catch(() => {}); // autoplay may be blocked silently
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            {/* Instant dark fallback — shows while video loads */}
            {!videoLoaded && (
                <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                        background: 'linear-gradient(135deg, #050505 0%, #050505 50%, #050505 100%)'
                    }}
                />
            )}
            <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                poster="/hero-poster.webp"
                onCanPlay={() => setVideoLoaded(true)}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: videoLoaded ? 1 : 0, transition: 'opacity 1s ease' }}
            >
                <track kind="captions" srcLang="en" label="English" default />
            </video>
        </>
    );
};

// --- HOME PAGE COMPONENT ---
const HomePage = () => (
    <>
        <Helmet>
          <title>Lab of Games | Premium Spatial Computing & Game Engineering</title>
          <meta name="description" content="We engineer high-fidelity digital experiences. From AAA multiplayer games to enterprise spatial computing and cinematic 3D configurators, we build the future." />
          <script type="application/ld+json">
            {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Lab of Games",
              "url": "https://labofgames.com",
              "logo": "https://labofgames.com/logo.webp",
              "description": "Premium spatial computing, game engineering, and cinematic 3D configurators. We partner with global brands to engineer high-fidelity digital experiences.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Jalandhar",
                "addressRegion": "Punjab",
                "addressCountry": "IN"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-7973152831",
                "contactType": "sales",
                "email": "info@labofgames.in"
              },
              "sameAs": [
                "https://linkedin.com/company/labofgames",
                "https://instagram.com/labofgames"
              ]
            }
            `}
          </script>
        </Helmet>
        {/* --- HERO SECTION --- */}
        <header className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden z-0 pt-32 pb-0">
          <div className="absolute inset-0 z-0">
             <HeroVideo />
             {/* Gradient overlays to blend text and transition into the white page below */}
             <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/20 pointer-events-none"></div>
             <div className="absolute bottom-0 w-full h-[15vh] bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none"></div>
          </div>
          
          <div className="relative z-10 text-center px-6 max-w-[1200px] mx-auto flex flex-col items-center flex-1 justify-center mt-[-10vh]">
            
            <span className="inline-block border border-white/20 bg-black/20 backdrop-blur-md px-5 py-2 rounded-full text-[9px] md:text-xs font-semibold tracking-wider text-white mb-6 uppercase">
                Trusted by Startups, Enterprises & Forward-Thinking Brands
            </span>
            
            <RevealText>
              <h1 className="text-4xl md:text-7xl lg:text-[75px] leading-[1.1] font-['Playfair_Display'] font-normal text-white mb-6 max-w-5xl mx-auto drop-shadow-md">
                Engineering the Future of <br />
                <span className="italic text-white">Digital Experiences</span>
              </h1>
            </RevealText>
            
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3 }}
               className="flex flex-col items-center"
            >
               <p className="text-white/90 text-sm md:text-lg leading-relaxed max-w-3xl mx-auto mb-10 font-sans font-light drop-shadow">
                   Lab of Games creates scalable digital products—from AI platforms and real-time 3D to immersive AR/VR experiences—empowering brands to innovate, engage, and grow.
               </p>
               
               <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
                   <a href="#contact" className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 bg-white text-black font-bold rounded-full text-sm md:text-base hover:bg-zinc-200 transition-colors shadow-lg flex items-center justify-center gap-2">
                         <PhoneCall size={16} /> Book a Free Consultation
                     </a>
                   <Link to="/portfolio" className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 bg-black/40 hover:bg-black/60 text-white border border-white/20 font-bold rounded-full text-sm md:text-base transition-colors backdrop-blur-md flex items-center justify-center gap-2">
                       <Play size={16} fill="currentColor" /> Explore Our Work
                   </Link>
               </div>
            </motion.div>
          </div>

        </header>

        {/* --- MAIN SCROLL CONTENT --- */}
        <div className="relative z-10 bg-transparent mb-0 pt-0">
          {/* Continuous Marquee Strip (Moved below fold for scroll visibility) */}
          <div className="relative w-full bg-black overflow-hidden flex py-5 border-y border-white/10 shadow-2xl z-20">
               {/* First track */}
               <div className="flex shrink-0 animate-marquee items-center gap-12 px-6">
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">AI Solutions</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">Unity & Unreal Experts</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">AR/VR Development</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">600+ Projects Delivered</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">100M+ App Downloads</span>
                   <span className="text-white/30">•</span>
               </div>
               {/* Second track for seamless loop */}
               <div className="flex shrink-0 animate-marquee items-center gap-12 px-6" aria-hidden="true">
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">AI Solutions</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">Unity & Unreal Experts</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">AR/VR Development</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">600+ Projects Delivered</span>
                   <span className="text-white/30">•</span>
                   <span className="text-white/80 text-base font-sans tracking-widest uppercase whitespace-nowrap">100M+ App Downloads</span>
                   <span className="text-white/30">•</span>
               </div>
          </div>
          <ProblemStatement />
          <StatsSection />
          <CoreServices />
          <WorkflowProcess />
          <TrustSection />
          <FeaturedWork />
          <FAQSection />
          <LeadCapture />
        </div>
    </>
);

// --- ANIMATED PAGE TRANSITIONS ---
const PageWrapper = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
    >
        {children}
    </motion.div>
);

const AnimatedRoutes = () => {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
                <Route path="/real-estate" element={<PageWrapper><RealEstate /></PageWrapper>} />
                <Route path="/ar-vr" element={<PageWrapper><ARVR /></PageWrapper>} />
                <Route path="/automotive" element={<PageWrapper><Automotive /></PageWrapper>} />
                <Route path="/game-development" element={<PageWrapper><GameDevelopment /></PageWrapper>} />
                <Route path="/ai-platforms" element={<PageWrapper><AiPlatforms /></PageWrapper>} />
                <Route path="/enterprise-interactive" element={<PageWrapper><EnterpriseInteractive /></PageWrapper>} />
                <Route path="/about" element={<PageWrapper><AboutUs /></PageWrapper>} />
                <Route path="/portfolio" element={<PageWrapper><Portfolio /></PageWrapper>} />
                <Route path="/portfolio/:slug" element={<PageWrapper><ProjectDetail /></PageWrapper>} />
                <Route path="/blogs" element={<PageWrapper><Blogs /></PageWrapper>} />
                <Route path="/blogs/:slug" element={<PageWrapper><BlogDetail /></PageWrapper>} />
                <Route path="/terms-of-service" element={<PageWrapper><TermsOfService /></PageWrapper>} />
                <Route path="/privacy-policy" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
            </Routes>
        </AnimatePresence>
    );
};

// --- MAIN APP ---
export default function LabOfGames() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesHovered, setIsServicesHovered] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  return (
    <HelmetProvider>
      <Router>
      <AnalyticsTracker />
      <Cursor />

      <div className="relative min-h-screen bg-white text-black">
        <AmbientBackground />
        
        <div className="fixed top-0 left-0 w-full z-50 flex flex-col items-center pointer-events-none">
          <nav className="w-full max-w-[1400px] px-6 md:px-10 py-4 flex justify-between items-center bg-black rounded-b-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.3)] pointer-events-auto relative z-50">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-2xl md:text-[28px] font-normal font-['Lobster'] cursor-pointer text-white tracking-wide flex items-center">
              Lab Of Games
            </Link>
            <div className="hidden lg:flex items-center gap-8">
                <NavLink to="/" className={({ isActive }) => `text-base font-medium cursor-pointer transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>Home</NavLink>
                
                <div 
                    className="relative group h-full flex items-center"
                    onMouseEnter={() => setIsServicesHovered(true)}
                    onMouseLeave={() => setIsServicesHovered(false)}
                >
                    <div className="flex items-center gap-1 text-base font-medium text-zinc-400 hover:text-white cursor-pointer transition-colors">
                        Services
                        <motion.div animate={{ rotate: isServicesHovered ? 180 : 0 }} transition={{ duration: 0.2 }}>
                            <ChevronDown size={14} />
                        </motion.div>
                    </div>

                    <AnimatePresence>
                        {isServicesHovered && (
                            <motion.div
                                initial={{ opacity: 0, y: 10, x: "-50%" }}
                                animate={{ opacity: 1, y: 0, x: "-50%" }}
                                exit={{ opacity: 0, y: 10, x: "-50%" }}
                                transition={{ duration: 0.2 }}
                                className="fixed top-[70px] left-1/2 pt-4 z-50"
                            >
                                <div className="w-[850px] bg-[#050505] border border-white/10 rounded-2xl p-6 shadow-2xl pointer-events-auto">
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                        <Link to="/game-development" className="flex gap-4 cursor-pointer group/item items-start p-3 rounded-xl hover:bg-white/5 transition-colors">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center group-hover/item:border-white/50 group-hover/item:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all overflow-hidden">
                                                <img src="/game-development.webp" alt="Game Development" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-white text-lg font-bold group-hover/item:text-white transition-colors">Game Development</div>
                                                <div className="text-white/80 text-base font-medium leading-relaxed">Full-cycle production and scalable multiplayer netcode</div>
                                            </div>
                                        </Link>

                                        <Link to="/ai-platforms" className="flex gap-4 cursor-pointer group/item items-start p-3 rounded-xl hover:bg-white/5 transition-colors">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center group-hover/item:border-white/50 group-hover/item:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all overflow-hidden">
                                                <img src="/ai-home.webp" alt="AI Platforms" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-white text-lg font-bold group-hover/item:text-white transition-colors">AI Platforms & Smart Apps</div>
                                                <div className="text-white/80 text-base font-medium leading-relaxed">Intelligent systems and automated backend solutions</div>
                                            </div>
                                        </Link>

                                        <Link to="/automotive" className="flex gap-4 cursor-pointer group/item items-start p-3 rounded-xl hover:bg-white/5 transition-colors">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center group-hover/item:border-white/50 group-hover/item:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all overflow-hidden">
                                                <img src="/automotive.webp" alt="Automotive Experiences" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-white text-lg font-bold group-hover/item:text-white transition-colors">Automotive Experiences</div>
                                                <div className="text-white/80 text-base font-medium leading-relaxed">Real-time car customizers and in-vehicle software</div>
                                            </div>
                                        </Link>

                                        <Link to="/real-estate" className="flex gap-4 cursor-pointer group/item items-start p-3 rounded-xl hover:bg-white/5 transition-colors">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center group-hover/item:border-white/50 group-hover/item:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all overflow-hidden">
                                                <img src="/real-estate.webp" alt="Real Estate Visualization" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-white text-lg font-bold group-hover/item:text-white transition-colors">Real Estate Visualization</div>
                                                <div className="text-white/80 text-base font-medium leading-relaxed">Interactive 3D tours and high-fidelity architectural rendering</div>
                                            </div>
                                        </Link>

                                        <Link to="/ar-vr" className="flex gap-4 cursor-pointer group/item items-start p-3 rounded-xl hover:bg-white/5 transition-colors">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center group-hover/item:border-white/50 group-hover/item:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all overflow-hidden">
                                                <img src="/arvr.webp" alt="AR / VR Development" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-white text-lg font-bold group-hover/item:text-white transition-colors">AR / VR Development</div>
                                                <div className="text-white/80 text-base font-medium leading-relaxed">Next-gen spatial computing and immersive training apps</div>
                                            </div>
                                        </Link>

                                        <Link to="/enterprise-interactive" className="flex gap-4 cursor-pointer group/item items-start p-3 rounded-xl hover:bg-white/5 transition-colors">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center group-hover/item:border-white/50 group-hover/item:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all overflow-hidden">
                                                <img src="/app-development.webp" alt="Enterprise Interactive Systems" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-white text-lg font-bold group-hover/item:text-white transition-colors">Enterprise Interactive Systems</div>
                                                <div className="text-white/80 text-base font-medium leading-relaxed">Custom applications and interactive web experiences</div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <NavLink to="/about" className={({ isActive }) => `text-base font-medium cursor-pointer transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>About Us</NavLink>
                <NavLink to="/portfolio" className={({ isActive }) => `text-base font-medium cursor-pointer transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>Our Work</NavLink>
                <NavLink to="/blogs" className={({ isActive }) => `text-base font-medium cursor-pointer transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>Blogs</NavLink>
            </div>
            <div className="hidden lg:flex items-center">
              <a href="/#contact" onClick={(e) => {
                  const contactEl = document.getElementById('contact');
                  if (contactEl) {
                      e.preventDefault();
                      contactEl.scrollIntoView({ behavior: 'smooth' });
                  }
              }} className="px-5 py-2.5 bg-white text-black font-bold rounded-xl text-[15px] hover:bg-zinc-200 transition-colors shadow-sm inline-block text-center">
                  Book a Free Consultation
              </a>
            </div>
            
            {/* Mobile Hamburger Toggle */}
            <button 
                aria-label="Toggle Mobile Menu"
                className="lg:hidden text-white p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>

          {/* Mobile Dropdown Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
                <motion.div 
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="w-full max-w-[1400px] mt-2 bg-black/95 backdrop-blur-xl rounded-[20px] p-6 flex flex-col gap-6 shadow-2xl pointer-events-auto border border-white/10 lg:hidden relative z-40"
                >
                    <div className="flex flex-col gap-4">
                        <NavLink to="/" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-xl font-medium transition-colors border-b border-white/10 pb-4 ${isActive ? "text-white" : "text-white hover:text-white"}`}>Home</NavLink>
                        
                        {/* Mobile Services Menu Accordion */}
                        <div className="flex flex-col border-b border-white/10 pb-4">
                            <div 
                                className="flex justify-between items-center cursor-pointer text-white hover:text-white transition-colors"
                                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                            >
                                <span className="text-xl font-medium">Services</span>
                                <motion.div animate={{ rotate: isMobileServicesOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                    <ChevronDown size={18} />
                                </motion.div>
                            </div>
                            <AnimatePresence>
                                {isMobileServicesOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden flex flex-col gap-3 mt-4 pl-4 border-l border-white/10 ml-2"
                                    >
                                        <Link to="/game-development" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-zinc-400 hover:text-white transition-colors pb-2 border-b border-white/5">Game Development</Link>
                                        <Link to="/ai-platforms" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-zinc-400 hover:text-white transition-colors pb-2 border-b border-white/5">AI Platforms & Smart Apps</Link>
                                        <Link to="/automotive" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-zinc-400 hover:text-white transition-colors pb-2 border-b border-white/5">Automotive Experiences</Link>
                                        <Link to="/real-estate" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-zinc-400 hover:text-white transition-colors pb-2 border-b border-white/5">Real Estate Visualization</Link>
                                        <Link to="/ar-vr" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-zinc-400 hover:text-white transition-colors pb-2 border-b border-white/5">AR / VR Development</Link>
                                        <Link to="/enterprise-interactive" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-zinc-400 hover:text-white transition-colors">Enterprise Interactive Systems</Link>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <NavLink to="/about" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-xl font-medium transition-colors border-b border-white/10 pb-4 ${isActive ? "text-white" : "text-white hover:text-white"}`}>About Us</NavLink>
                        <NavLink to="/portfolio" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-xl font-medium transition-colors border-b border-white/10 pb-4 ${isActive ? "text-white" : "text-white hover:text-white"}`}>Our Work</NavLink>
                        <NavLink to="/blogs" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-xl font-medium transition-colors pb-2 ${isActive ? "text-white" : "text-white hover:text-white"}`}>Blogs</NavLink>
                    </div>
                    <a href="/#contact" onClick={(e) => {
                        const contactEl = document.getElementById('contact');
                        if (contactEl) {
                            e.preventDefault();
                            setIsMobileMenuOpen(false);
                            contactEl.scrollIntoView({ behavior: 'smooth' });
                        } else {
                            setIsMobileMenuOpen(false);
                        }
                    }} className="w-full py-4 bg-white text-black font-bold rounded-xl text-lg hover:bg-zinc-200 transition-colors shadow-sm mt-2 block text-center">
                          Book a Free Consultation
                      </a>
                </motion.div>
            )}
          </AnimatePresence>
        </div>

        <Suspense fallback={<div style={{minHeight:'100vh',background:'#000'}} />}>
          <AnimatedRoutes />
        </Suspense>

        <FoundationFooter />
      </div>
    </Router>
    </HelmetProvider>
  );
}